import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Select } from '@ngxs/store';
import { TransportServiceRequest } from 'planning-portal-library/src/lib/core/domain';
import { Service } from 'planning-portal-library/src/lib/core/domain/models/service';
import { DeleteServiceRequest } from 'planning-portal-library/src/lib/core/domain/requests/services/delete-service.request';
import { GetServicesRequest } from 'planning-portal-library/src/lib/core/domain/requests/services/get-services.request';
import { ServiceRequest } from 'planning-portal-library/src/lib/core/domain/requests/services/service.request';
import { injectEnvironment } from 'planning-portal-library/src/lib/core/environments/environment.token';
import { ServicesRepository } from 'planning-portal-library/src/lib/core/repositories/service.repository';
import { ServicesState } from 'planning-portal-library/src/lib/presentation/services/state/services.state';
import {
  catchError,
  forkJoin,
  map,
  mergeMap,
  Observable,
  reduce,
  share,
} from 'rxjs';
import { BaseGridResponse } from '../../../shared/base-grid.response';
import { BaseRequest } from '../../../shared/base.request';
import { BaseResponse } from '../../../shared/base.response';
import { ServiceWebEntity } from './entities/service-web-entity';
import { ServiceWebRepositoryMapper } from './entities/service-web-repository-mapper';
import { ServiceApiUrls } from './service-api-urls';

@Injectable({ providedIn: 'root' })
export class ServiceWebRepository extends ServicesRepository {
  private readonly serviceMapper = new ServiceWebRepositoryMapper();
  @Select(ServicesState.Services) service$: Observable<Service[]> | undefined;

  services: ServiceWebEntity[] = [];

  private readonly environment = injectEnvironment();

  constructor(private readonly http: HttpClient) {
    super();
  }

  getServices(
    request: BaseRequest<GetServicesRequest>
  ): Observable<BaseGridResponse<Service>> {
    const httpMap = this.http
      .post<BaseResponse<BaseGridResponse<ServiceWebEntity>>>(
        `${this.environment.url}${ServiceApiUrls.GetServices}`,
        request.request
      )
      .pipe(share());

    const dataResultMap = httpMap
      .pipe(
        mergeMap((response) => response.result!.data),
        catchError((err) => {
          throw err;
        })
      )
      .pipe(map(this.serviceMapper.mapFrom))
      .pipe(reduce((items: Service[], item: Service) => [...items, item], []));

    const totalRecordsResultMap = httpMap.pipe(
      map((response) => response.result!.totalRecords)
    );

    return forkJoin([dataResultMap, totalRecordsResultMap]).pipe(
      map((input) => {
        const item: BaseGridResponse<Service> = {
          data: input[0],
          totalRecords: +input[1],
        };
        return item;
      })
    );
  }

  addService(request: ServiceRequest[]): Observable<boolean> {
    return this.http
      .post<BaseResponse<boolean>>(
        `${this.environment.url}${ServiceApiUrls.CreateEditService}`,
        request
      )
      .pipe(
        map((data: BaseResponse<boolean>) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  addOtherDistributorsService(request: ServiceRequest[]): Observable<boolean> {
    return this.http
      .post<BaseResponse<unknown>>(
        `${this.environment.url}${ServiceApiUrls.CreateOtherDistributionLaneService}`,
        request
      )
      .pipe(
        map((data) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  addEditTransportService(
    request: TransportServiceRequest[]
  ): Observable<boolean> {
    return this.http
      .post<BaseResponse<boolean>>(
        `${this.environment.url}${ServiceApiUrls.CreateEditTransportService}`,
        request
      )
      .pipe(
        map((data: BaseResponse<boolean>) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  editService(request: ServiceRequest): Observable<boolean> {
    return this.http
      .post<BaseResponse<boolean>>(
        `${this.environment.url}${ServiceApiUrls.CreateEditService}`,
        request
      )
      .pipe(
        map((data: BaseResponse<boolean>) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  deleteService(request: DeleteServiceRequest): Observable<boolean> {
    return this.http
      .post<BaseResponse<boolean>>(
        `${this.environment.url}${ServiceApiUrls.DeleteService}`,
        request
      )
      .pipe(
        map((data: any) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }
}
